import { type ReactNode, useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { BildirimZili } from '@/components/bildirim/BildirimZili'
import {
  LogOut, Crown, Home, Building2, Users, Briefcase,
  Calendar, CalendarDays, Cake, Coins, Settings, ChevronLeft, Award, CreditCard,
  Menu, X, Banknote, Shield, ArrowLeftRight, ClipboardList,
} from 'lucide-react'
import clsx from 'clsx'

interface BossLayoutProps {
  children?: ReactNode
}

interface MenuItem {
  to: string
  icon: typeof Home
  label: string
  aktif: boolean
}

const MENU: MenuItem[] = [
  { to: '/boss', icon: Home, label: 'Genel Bakış', aktif: true },
  { to: '/boss/subeler', icon: Building2, label: 'Şubeler', aktif: true },
  { to: '/boss/pozisyonlar', icon: Briefcase, label: 'Pozisyonlar', aktif: true },
  { to: '/boss/personel', icon: Users, label: 'Personel', aktif: true },
  { to: '/boss/vardiyalar', icon: Calendar, label: 'Vardiyalar', aktif: true },
  { to: '/boss/gorevler', icon: ClipboardList, label: 'Görevler', aktif: true },
  { to: '/boss/giris-cikis', icon: ArrowLeftRight, label: 'Giriş / Çıkış', aktif: true },
  { to: '/boss/puantajlar', icon: Award, label: 'Puantajlar', aktif: true },
  { to: '/boss/avanslar', icon: CreditCard, label: 'Avanslar', aktif: true },
  { to: '/boss/izinler', icon: Calendar, label: 'İzinler', aktif: true },
  { to: '/boss/tatiller', icon: CalendarDays, label: 'Resmi Tatiller', aktif: true },
  { to: '/boss/dogumgunleri', icon: Cake, label: 'Doğum Günleri', aktif: true },
  { to: '/boss/maliyet', icon: Coins, label: 'Maliyet', aktif: true },
  { to: '/boss/maaslar', icon: Banknote, label: 'Maaşlar', aktif: true },
  { to: '/boss/loglar', icon: Shield, label: 'Sistem Logları', aktif: true },
  { to: '/boss/ayarlar', icon: Settings, label: 'Ayarlar', aktif: true },
]

const MOBIL_HIZLI_TOLAR = ['/boss', '/boss/personel', '/boss/vardiyalar', '/boss/maliyet']

export function BossLayout({ children }: BossLayoutProps) {
  const { kullanici, cikisYap } = useAuth()
  const [sidebarAcik, setSidebarAcik] = useState(true)
  const [drawerAcik, setDrawerAcik] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setDrawerAcik(false)
  }, [location.pathname])

  if (!kullanici) return null

  const aktifMenu = MENU.filter((m) => m.aktif)
  const hizliMenu = aktifMenu.filter((m) => MOBIL_HIZLI_TOLAR.includes(m.to))

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside
        className={clsx(
          'hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-200',
          sidebarAcik ? 'w-64' : 'w-16'
        )}
      >
        <div className="px-4 py-5 border-b border-gray-200 flex items-center justify-between">
          {sidebarAcik && (
            <div className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-brand-600" />
              <span className="font-bold text-gray-900">Shift Beat</span>
            </div>
          )}
          <button
            onClick={() => setSidebarAcik(!sidebarAcik)}
            className="p-1 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className={clsx(
              'w-5 h-5 text-gray-500 transition-transform',
              !sidebarAcik && 'rotate-180'
            )} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {MENU.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/boss'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-4 py-3 mx-2 rounded-xl transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50',
                  !item.aktif && 'opacity-40 pointer-events-none'
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarAcik && (
                <span className="text-sm">
                  {item.label}
                  {!item.aktif && <span className="ml-1 text-xs text-gray-400">(yakında)</span>}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-gray-200 p-3">
          {sidebarAcik ? (
            <div className="space-y-2">
              <div className="px-2 py-1 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-gray-500">BOSS</p>
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {kullanici.isim}
                  </p>
                </div>
                <BildirimZili kullaniciId={kullanici.id} />
              </div>
              <button
                onClick={cikisYap}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <BildirimZili kullaniciId={kullanici.id} />
              <button onClick={cikisYap} className="p-2 hover:bg-gray-50 rounded-lg w-full flex justify-center" title="Çıkış">
                <LogOut className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-brand-600" />
          <span className="font-bold text-gray-900 text-sm">Shift Beat</span>
        </div>
        <div className="flex items-center gap-1">
          <BildirimZili kullaniciId={kullanici.id} />
          <button onClick={cikisYap} className="p-2 hover:bg-gray-50 rounded-lg" title="Çıkış">
            <LogOut className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-20 grid grid-cols-5 gap-1 px-2 py-2">
        {hizliMenu.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/boss'}
            className={({ isActive }) =>
              clsx(
                'flex flex-col items-center gap-1 py-2 rounded-lg transition-colors',
                isActive ? 'text-brand-700' : 'text-gray-500'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] font-semibold">{item.label}</span>
          </NavLink>
        ))}
        <button
          onClick={() => setDrawerAcik(true)}
          className="flex flex-col items-center gap-1 py-2 rounded-lg transition-colors text-gray-500 hover:text-brand-700"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-semibold">Daha</span>
        </button>
      </div>

      {drawerAcik && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setDrawerAcik(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-xl p-4 max-h-[85vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-brand-600" />
                <h3 className="font-bold text-gray-900">Tüm Menü</h3>
              </div>
              <button
                onClick={() => setDrawerAcik(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {MENU.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/boss'}
                  className={({ isActive }) =>
                    clsx(
                      'flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors',
                      isActive
                        ? 'bg-brand-50 border-brand-200 text-brand-700'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
                      !item.aktif && 'opacity-40 pointer-events-none'
                    )
                  }
                >
                  <item.icon className="w-6 h-6" />
                  <span className="text-xs font-semibold text-center">{item.label}</span>
                  {!item.aktif && (
                    <span className="text-[9px] text-gray-400 -mt-1">yakında</span>
                  )}
                </NavLink>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-gray-500 uppercase">BOSS</p>
                <p className="font-semibold text-gray-900 text-sm">{kullanici.isim}</p>
              </div>
              <button
                onClick={cikisYap}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg"
              >
                <LogOut className="w-4 h-4" />
                <span>Çıkış</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="max-w-6xl mx-auto px-4 py-6 md:px-8">
          {children ?? <Outlet />}
        </div>
      </main>
    </div>
  )
}
